import { DEFAULT_ITEMS_PER_PAGE } from '@/core/sourcing/search/consts/default-pagination.const';
import type { SearchMode } from '@/core/sourcing/search/types/search-mode.type';
import type { SelectedFilterType } from '@/core/sourcing/search/types/selected-filter-type.type';
import type {
  FilterableLanguage,
  FilterableResponsiveness,
  FilterableShift,
} from '@/core/sourcing/search/consts/hard-filters.const';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { JobWithRelations } from '@factoryfixinc/ats-interfaces';
import type { JobLocationCoordinates } from '@/core/shared/job/job.persistence';

export interface DossierFilterState {
  inferred: boolean;
  includeUnknown: boolean;
  /** A recruiter edit took over an inferred value; clearing it still suppresses re-inference. */
  takenOver?: boolean;
}

export const useSearchStore = defineStore(
  'sharedSearch',
  () => {
    // Loading
    const isSearchingCandidates = ref<boolean>(false);

    // Form Data
    const keyword = ref<string | undefined>(undefined);
    const jobTitle = ref<string | undefined>(undefined);
    const skills = ref<string | undefined>(undefined);
    const machines = ref<string | undefined>(undefined);
    const brands = ref<string | undefined>(undefined);
    const lastActive = ref<number | undefined>(undefined);
    const location = ref<string | undefined>(undefined);
    const distance = ref<number | undefined>(undefined);

    // Dossier-derived hard-filter staging, persisted like the other sidebar filters.
    const payRateMin = ref<number | undefined>(undefined);
    const payRateMax = ref<number | undefined>(undefined);
    const shifts = ref<FilterableShift[]>([]);
    const willingToRelocate = ref<boolean>(false);
    const openToTravel = ref<boolean>(false);
    const languages = ref<FilterableLanguage[]>([]);
    const responsiveness = ref<FilterableResponsiveness[]>([]);
    const recentlyOpenToWork = ref<boolean>(false);

    // Per-type ownership and mode for the insight filters above, present only beside an active
    // value. No entry means user-set and strict, which is also how state persisted before this
    // field existed reads back. Pay keeps separate MIN/MAX entries: the bounds can differ.
    const dossierFilterState = ref<Partial<Record<SelectedFilterType, DossierFilterState>>>({});

    // Rerun attribution (one-shot): set when re-running a recent search, consumed by the next
    // request in syncFiltersToSourcingSearch. Not persisted — a fresh load is a 'typed' search.
    const source = ref<'typed' | 'recent'>('typed');
    const parentSessionId = ref<string | null>(null);

    // Active job for "search from a job" mode. Holds the full fetched job so the
    // request composer can build the document-mode search. Omitted from persistence
    // so a fresh page load returns to typed search (job mode is session-sticky only).
    const activeJob = ref<JobWithRelations<'jobTitle'> | undefined>(undefined);

    // Resolved coordinates for the active job's location (from ff-rest). Lets job mode
    // fence the geo filter on a point rather than a geocoded string (ONE-6241). Omitted
    // from persistence alongside activeJob; undefined when the job has no resolvable location.
    const activeJobCoordinates = ref<JobLocationCoordinates | undefined>(undefined);

    // UI search-mode intent driving the request: 'natural'/'keyword_boolean' for typed
    // searches, 'job' for a job search (both set from the search view on submit), undefined
    // on the legacy keyword path. The request itself still derives job mode from `activeJob`,
    // so clearing the job must also reset this (ONE-6903). Session-only: omitted from
    // persistence so a fresh load resets to the default.
    const searchMode = ref<SearchMode | undefined>(undefined);

    // FE-local: the current sidebar value was inferred — from the active job (job mode,
    // LOCATION only) or from natural-language extraction in the search response (NL mode).
    // Each renders a sparkle next to its label and is cleared when the user edits that field.
    const locationInferred = ref<boolean>(false);
    const distanceInferred = ref<boolean>(false);
    const lastActiveInferred = ref<boolean>(false);

    // Inferred filter types the recruiter removed (X on a sparkled filter), valid for the
    // query they were removed under: re-running the same query won't re-infer them, while
    // editing the query drops the suppression (ONE-6178). Session-only.
    const suppressedInferredTypes = ref<SelectedFilterType[]>([]);
    const suppressedForKeyword = ref<string | undefined>(undefined);
    const page = ref<number | undefined>(1);
    const itemsPerPage = ref<number | undefined>(DEFAULT_ITEMS_PER_PAGE);

    // Layout
    const candidateListScrollTop = ref<number>(0);
    const alreadyExecutedASearch = ref<boolean>(false);

    // alphabetized list
    return {
      activeJob,
      activeJobCoordinates,
      alreadyExecutedASearch,
      brands,
      candidateListScrollTop,
      distance,
      distanceInferred,
      dossierFilterState,
      isSearchingCandidates,
      itemsPerPage,
      jobTitle,
      keyword,
      languages,
      lastActive,
      lastActiveInferred,
      location,
      locationInferred,
      machines,
      openToTravel,
      page,
      parentSessionId,
      payRateMax,
      payRateMin,
      recentlyOpenToWork,
      responsiveness,
      searchMode,
      shifts,
      skills,
      source,
      suppressedForKeyword,
      suppressedInferredTypes,
      willingToRelocate,
    };
  },
  {
    persist: {
      // The *Inferred flags persist alongside their values (location/distance/lastActive):
      // a persisted value without its flag reads as user-set on the next load, which fences
      // the search and blocks NL re-inference until the filter is cleared. Keeping them in
      // sync (here for in-app navigation, plus the `inferred` URL param for reloads and
      // deep-links) lets an AI-inferred filter stay inferred and re-infer on the next query.
      omit: [
        'alreadyExecutedASearch',
        'activeJob',
        'activeJobCoordinates',
        'source',
        'parentSessionId',
        'searchMode',
        'suppressedInferredTypes',
        'suppressedForKeyword',
      ],
    },
  },
);
